import dayjs, { Dayjs } from 'dayjs';
import { Trans, useTranslation } from 'react-i18next';
import { WEALTH_GOAL } from '../../common/constants';
import { useAppSelector } from '../../common/hooks';
import { WealthGoalsFormType, GoalTypeProps } from '../../common/types/clients-entity';
import { ConfigurableDrawer, FormField } from '../../components/organisms/GoalDrawer';
import { ComboSelect, Typography } from '../../components/atoms';
import { priorityOptionsFlow2, getGoalPriorityEnum, getPriorityLabel, isAspirePriorityTenant } from '../../utils';
import { GoalPriorityInfoText } from '../../components/molecules/GoalPriorityInfoText/GoalPriorityInfoText';
import { Box, useTheme } from '@mui/material';
import { ReactComponent as InfoIcon } from '../../assets/icons/info-blue.svg';

export interface WealthGoalProps {
    // eslint-disable-next-line no-unused-vars
    handleClose: (flag: boolean) => void;
    onSubmit?: () => void;
    loading?: boolean;
    heading?: string;
    subHeading?: string;
    selectedGoal?: boolean | GoalTypeProps;
    goalData?: any;
    open: boolean;
    currency?: string;
}

export const WealthGoals = ({ handleClose, selectedGoal, open, goalData }: WealthGoalProps) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const { goeConfig } = useAppSelector((state) => state.global);
    const goalPriorityValue = goeConfig?.['goalPriorityProbabilities'];
    const goalPriorityValueInPercentage =
        goalPriorityValue &&
        Object.fromEntries(
            Object.entries(goalPriorityValue || {}).map(([key, value]) => [key, (value as number) * 100])
        );
    const initialValues: WealthGoalsFormType = {
        goalAmount: 0,
        targetDate: null as Dayjs | null,
        goalType: 'Wealth Goal',
        goalId: '',
        goalName: '',
        goalPriority: undefined
    };
    const goalPriorityEnum = getGoalPriorityEnum(goalPriorityValue);
    const priorityOptions = priorityOptionsFlow2(t, goalPriorityEnum);
    const tenant = useAppSelector((state) => state?.auth?.user?.tenant);
    const getPriorityDisplayLabel = (label: string, value: string) => getPriorityLabel({ label, value }, tenant, t);
    const fields: FormField<WealthGoalsFormType>[] = [
        {
            name: 'goalName',
            label: t('TEXT_GOAL_NAME'),
            type: 'text',
            placeholder: t('TEXT_GOAL_NAME'),
            required: true,
            validation: {
                pattern: /^(?!\s)(?!.*\s$)([A-Za-z0-9\s’'.\-&/+()áéíóúüñÁÉÍÓÚÜÑàâäèêëîïôöùûçÀÂÄÈÊËÎÏÔÖÙÛÇ])*$/i
            }
        },
        {
            name: 'goalPriority',
            label: t('TEXT_GOAL_PRIORITY'),
            type: 'select',
            required: true,
            customRender: ({ field, error, onChange }) => {
                return (
                    <ComboSelect
                        onSelectChange={(val) => {
                            onChange && onChange(val);
                        }}
                        onBlur={field.onBlur}
                        selectedValue={(field.value as string) || ''}
                        name={`wealth-goal-priority-${field.name}`}
                        placeholder={t('PRIORITY_TEXT_SELECT') + `(${priorityOptions.length})`}
                        options={priorityOptions}
                        error={!!error}
                        listItemStyle={{
                            padding: '0px 14px'
                        }}
                        renderMenuItem={(option, index = 0) => (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                                {`${index + 1} - ${getPriorityDisplayLabel(option.label, option.value)}`}
                            </Box>
                        )}
                        renderValue={(selected) => {
                            const selectedOption = priorityOptions.find((option) => option.value === selected);
                            if (selectedOption) {
                                return (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                        {`${selectedOption.index + 1} - ${getPriorityDisplayLabel(
                                            selectedOption.label,
                                            selectedOption.value
                                        )}`}
                                    </Box>
                                );
                            }
                            return (
                                <em style={{ color: theme.palette.info[300] }}>
                                    {t('PRIORITY_TEXT_SELECT') + ` (${priorityOptions.length})`}
                                </em>
                            );
                        }}
                    />
                );
            },
            infoTooltip: <GoalPriorityInfoText values={goalPriorityValueInPercentage} renameWishToAspire={isAspirePriorityTenant(tenant)} />
        },
        {
            name: 'goalAmount',
            label: t('TEXT_GOAL_TARGET_AMOUNT'),
            type: 'currency',
            required: true,
            validation: {
                validate: (value) => {
                    if (!value) {
                        return t('TEXT_THIS_IS_REQUIRED_FIELD');
                    }
                    return true;
                }
            },
            infoTooltip: (
                <Trans i18nKey="TEXT_GOAL_TARGET_AMOUNT_INFO_TEXT" components={{ strong: <strong />, br: <br /> }} />
            ),
            HelperTextComponent: () => (
                <Box mt="4px" display="flex" alignItems="center" gap="4px">
                    <InfoIcon />
                    <Typography
                        variant="labels.small"
                        sx={{ color: theme.palette.info[300], textDecoration: 'underline' }}
                        label={t('TEXT_GOAL_TARGET_AMOUNT_HELPER_TEXT')}
                    />
                </Box>
            )
        },
        {
            name: 'targetDate',
            label: t('GOAL_SUMMARY_TARGET_DATE_TEXT'),
            type: 'date',
            required: true,
            datePickerStartDate: () => dayjs().add(1, 'month').startOf('month').format(),
            validation: {
                validate: (value) => {
                    if (!value) {
                        return t('TEXT_THIS_IS_REQUIRED_FIELD');
                    }

                    const today = dayjs();
                    const oneMonthFromToday = today.add(1, 'month');
                    const dateValue = dayjs(value);

                    if (dateValue.isBefore(dayjs(), 'day')) {
                        return t('TEXT_VALID_FUTURE_DATE');
                    }
                    if (!dateValue.isAfter(oneMonthFromToday)) {
                        return t('TEXT_DATE_AT_LEAST_ONE_MONTH');
                    }
                    return dateValue.isValid();
                }
            },
            isMMYYYY: () => true,
            infoTooltip: (
                <Trans
                    i18nKey="GOAL_SUMMARY_TARGET_DATE_TEXT_INFO_TEXT"
                    components={{ strong: <strong />, br: <br /> }}
                />
            )
        }
    ];

    return (
        <ConfigurableDrawer<WealthGoalsFormType>
            open={open}
            handleClose={handleClose}
            selectedGoal={selectedGoal}
            goalData={goalData}
            goalType={WEALTH_GOAL}
            fields={fields}
            defaultValues={initialValues}
            drawerLabel={t('BANORTE_WEALTH_GOAL_DRAWER_LABEL')}
        />
    );
};
